import { makeStyles, createStyles, Theme } from '@material-ui/core';

export const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === 'dark';

  const lightColors = {
    /* Primary Colors */
    '--color-primary': '#003F2D',
    '--color-primary-04': 'rgba(0, 63, 45, 0.04)',
    '--color-primary-05': 'rgba(0, 63, 45, 0.05)',
    '--color-primary-06': 'rgba(0, 63, 45, 0.06)',
    '--color-primary-08': 'rgba(0, 63, 45, 0.08)',
    '--color-primary-10': 'rgba(0, 63, 45, 0.10)',
    '--color-primary-12': 'rgba(0, 63, 45, 0.12)',
    '--color-primary-14': 'rgba(0, 63, 45, 0.14)',
    '--color-primary-15': 'rgba(0, 63, 45, 0.15)',
    '--color-primary-20': 'rgba(0, 63, 45, 0.20)',
    '--color-primary-25': 'rgba(0, 63, 45, 0.25)',
    '--color-primary-30': 'rgba(0, 63, 45, 0.30)',
    '--color-primary-35': 'rgba(0, 63, 45, 0.35)',
    '--color-primary-40': 'rgba(0, 63, 45, 0.40)',
    '--color-primary-dark': '#002d21',
    '--color-primary-darker': '#002820',

    /* Background Colors */
    '--background-bg-layer-1': '#FFF',
    '--background-light': '#f5f5f5',

    /* Text Colors */
    '--text-text-default': '#1A1A1A',
    '--text-text-subtle': '#5A5A5A',
    '--text-text-placeholder': '#6C6C6C',
    '--text-header-primary': '#202728',
    '--text-disabled': '#cccccc',

    /* Border & Divider Colors */
    '--outlines-divider': 'rgba(26, 26, 26, 0.12)',
    '--outlines-subtle': 'rgba(26, 26, 26, 0.08)',

    /* Hover & Active States */
    '--color-hover-light': 'rgba(26, 26, 26, 0.06)',
    '--color-hover-medium': 'rgba(26, 26, 26, 0.10)',
    '--color-hover-state-20': 'rgba(26, 26, 26, 0.20)',
    '--color-active-state-30': 'rgba(26, 26, 26, 0.30)',

    /* Overlays & Shadows */
    '--overlay-dark': 'rgba(0, 0, 0, 0.30)',
    '--overlay-dark-50': 'rgba(0, 0, 0, 0.50)',
    '--shadow-light': 'rgba(0, 0, 0, 0.12)',
    '--shadow-light-05': 'rgba(0, 0, 0, 0.05)',
    '--shadow-light-15': 'rgba(0, 0, 0, 0.15)',
    '--shadow-light-80': 'rgba(0, 0, 0, 0.80)',

    /* Icon Colors */
    '--icon-color-primary': '#1a1a1a',
    '--icon-color-secondary': '#5a5a5a',
    '--icon-color-muted': '#999999',
  };

  const darkChatBotColors = {
    '--eds-ai-chat-bot-background': '#333333',
    '--eds-ai-chat-bot-background-alt': '#424242',
    '--eds-ai-chat-bot-surface': '#424242',
    '--eds-ai-chat-bot-bg-hover': 'rgba(255, 255, 255, 0.08)',
    '--eds-ai-chat-bot-bg-selected': 'rgba(255, 255, 255, 0.16)',
    '--eds-ai-chat-bot-color-white': '#424242',
    '--eds-ai-chat-bot-text-primary': '#FFFFFF',
    '--eds-ai-chat-bot-text-secondary': '#CCCCCC',
    '--eds-ai-chat-bot-text-hint': '#CCCCCC',
    '--eds-ai-chat-bot-button-text': '#17E88F',
    '--eds-ai-chat-bot-input-border': '#767676',
    '--eds-ai-chat-bot-input-search-border': '#767676',
    '--eds-ai-chat-bot-color-error': '#FF543E',
    '--eds-ai-chat-bot-overlay-backdrop': 'rgba(0, 0, 0, 0.5)',
    '--eds-ai-chat-bot-shadow-card-hover': 'rgba(0, 0, 0, 0.3)',
    '--eds-ai-chat-bot-icon-default-alt': '#A1A19C',
    '--eds-ai-chat-bot-state-hover': 'rgba(23, 232, 143, 0.12)',
    '--eds-ai-chat-bot-attachment-border': 'rgba(255, 255, 255, 0.12)',
    '--eds-ai-chat-bot-attachment-border-hover': 'rgba(23, 232, 143, 0.35)',
    '--eds-ai-chat-bot-attachment-hover-bg': '#404040',
    '--eds-ai-chat-bot-remove-icon-bg': '#5c5c5c',
    '--eds-ai-chat-bot-remove-icon-hover-bg': '#767676',
    '--eds-ai-chat-bot-hint-tag-bg': 'rgba(23, 232, 143, 0.15)',
    '--eds-ai-chat-bot-hint-tag-bg-hover': 'rgba(23, 232, 143, 0.24)',
    '--eds-ai-chat-bot-scrollbar-thumb': '#5c5c5c',
    '--eds-ai-chat-bot-scrollbar-thumb-hover': '#767676',
    '--eds-ai-chat-bot-scrollbar-track-dark': 'rgba(255, 255, 255, 0.06)',
    '--eds-ai-chat-bot-scrollbar-thumb-dark': 'rgba(255, 255, 255, 0.28)',
    '--eds-ai-chat-bot-scrollbar-thumb-dark-hover': 'rgba(255, 255, 255, 0.42)',
    '--eds-ai-chat-bot-shell-bg': '#333333',
    '--eds-ai-chat-bot-welcome-text': '#17E88F',
    '--eds-ai-chat-bot-prompt-bg': '#424242',
    '--eds-ai-chat-bot-bubble-bg': '#424242',
    '--eds-ai-chat-bot-bubble-user-bg': 'rgba(23, 232, 143, 0.12)',
    '--eds-ai-chat-bot-input-bg': '#424242',
    '--eds-ai-chat-bot-input-focus-outline': '#17E88F',
    '--eds-ai-chat-bot-settings-heading-bg': '#424242',
    '--eds-ai-chat-bot-font-size-label': '14px',
    '--eds-ai-chat-bot-line-height': '20px',
    '--eds-ai-chat-bot-letter-spacing': '0',
    '--eds-ai-chat-bot-text-default': '#FFFFFF',
    '--eds-ai-chat-bot-text-subtle': '#CCCCCC',
    '--eds-ai-chat-bot-text-placeholder': '#A1A19C',
    '--eds-ai-chat-bot-divider': 'rgba(255, 255, 255, 0.12)',
    '--eds-ai-chat-bot-outline-divider': 'rgba(255, 255, 255, 0.24)',
    '--eds-ai-chat-bot-sidebar-border': '#767676',
    '--eds-ai-chat-bot-thinking-bg': '#3E3E3E',
    '--eds-ai-chat-bot-thinking-border': 'rgba(255, 255, 255, 0.12)',
    '--eds-ai-chat-bot-thinking-label-color': '#CCCCCC',
    '--eds-ai-chat-bot-thinking-status-color': '#A1A19C',
  };

  // Layout Tokens
  const layoutTokens = {
    '--eds-ai-chat-bot-max-content-width': '80%',
  };

  const darkColors = {
    /* Primary Colors */
    '--color-primary': '#17E88F',
    '--color-primary-06': 'rgba(0, 208, 132, 0.06)',
    '--color-primary-08': 'rgba(0, 208, 132, 0.08)',
    '--color-primary-10': 'rgba(0, 208, 132, 0.10)',
    '--color-primary-12': 'rgba(0, 208, 132, 0.12)',
    '--color-primary-15': 'rgba(0, 208, 132, 0.15)',
    '--color-primary-20': 'rgba(0, 208, 132, 0.20)',
    '--color-primary-dark': '#17E88F',
    '--color-primary-darker': '#0fbf74',

    /* Background Colors */
    '--background-bg-layer-1': '#333333',
    '--background-light': '#424242',
    '--bg-input': '#333333',
    '--bg-tooltip': '#292929',

    /* Text Colors */
    '--text-text-default': '#FFFFFF',
    '--text-text-subtle': '#CCCCCC',
    '--text-text-placeholder': '#A1A19C',

    '--text-header-primary': '#FFFFFF',
    '--text-disabled': '#555555',
    '--text-section-title': '#808080',
    '--text-outline': '#666666',

    /* Border & Divider Colors */
    '--outlines-divider': 'rgba(255, 255, 255, 0.12)',
    '--outlines-subtle': 'rgba(255, 255, 255, 0.08)',

    /* Hover & Active States */
    '--color-hover-light': 'rgba(255, 255, 255, 0.08)',
    '--color-hover-medium': 'rgba(255, 255, 255, 0.16)',
    '--color-hover-state-20': 'rgba(255, 255, 255, 0.20)',
    '--color-active-state-30': 'rgba(255, 255, 255, 0.30)',

    /* Overlays & Shadows */
    '--overlay-dark': 'rgba(0, 0, 0, 0.30)',
    '--overlay-dark-50': 'rgba(0, 0, 0, 0.50)',
    '--shadow-light': 'rgba(0, 0, 0, 0.12)',
    '--shadow-light-05': 'rgba(0, 0, 0, 0.05)',
    '--shadow-light-15': 'rgba(0, 0, 0, 0.15)',
    '--shadow-light-80': 'rgba(0, 0, 0, 0.80)',

    /* Icon Colors */
    '--icon-color-primary': '#FFFFFF',
    '--icon-color-secondary': '#A1A19C',
    '--icon-color-muted': '#727272',

    /* Dark Mode Accents - Teal */
    '--color-accent-teal': '#17E88F',
    '--color-accent-teal-hover': '#3CD278',
    '--color-accent-teal-10': 'rgba(23, 232, 143, 0.10)',
    '--color-accent-teal-12': 'rgba(23, 232, 143, 0.12)',
    '--color-accent-teal-15': 'rgba(23, 232, 143, 0.15)',
    '--color-accent-teal-18': 'rgba(23, 232, 143, 0.18)',
    '--color-accent-teal-20': 'rgba(23, 232, 143, 0.20)',
    '--color-accent-teal-25': 'rgba(23, 232, 143, 0.25)',
    '--color-accent-teal-30': 'rgba(23, 232, 143, 0.30)',

    /* Dark Mode Success - Green */
    '--color-success-green-15': 'rgba(76, 175, 80, 0.15)',
    '--color-success-green-20': 'rgba(76, 175, 80, 0.20)',
    '--color-success-green-25': 'rgba(76, 175, 80, 0.25)',
    '--color-success-green-30': 'rgba(76, 175, 80, 0.30)',
  };

  return createStyles({
    root: {
      ...(isDark ? darkColors : lightColors),
      ...(isDark ? darkChatBotColors : {}),
      ...layoutTokens,
    } as any,
  });
});
